import type { Board, GenerateOptions } from '../core/board';

const cancelled = () => new DOMException('Generation cancelled', 'AbortError');
export type Generator = (options: GenerateOptions, signal?: AbortSignal, deadline?: number) => Promise<Board>;

export function generateInWorker(options: GenerateOptions, makeWorker: () => Worker, signal?: AbortSignal,
  deadline = performance.now() + 2000): Promise<Board> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(cancelled()); return; }
    const remaining = deadline - performance.now();
    if (remaining <= 0) { reject(new Error('Generation deadline exceeded')); return; }
    let worker: Worker | undefined, timer: ReturnType<typeof setTimeout> | undefined, settled = false;
    const finish = (board?: Board, error?: Error) => {
      if (settled) return;
      settled = true; clearTimeout(timer); signal?.removeEventListener('abort', abort); worker?.terminate();
      if (board && performance.now() < deadline && !signal?.aborted) resolve(board);
      else reject(error ?? (signal?.aborted ? cancelled() : new Error('Generation deadline exceeded')));
    };
    const abort = () => finish(undefined, cancelled());
    try {
      worker = makeWorker();
      timer = setTimeout(() => finish(undefined, new Error('Generation timeout')), Math.max(0, deadline - performance.now()));
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) { abort(); return; }
      worker.onmessage = event => finish(event.data.board, event.data.error ? new Error(event.data.error) : undefined);
      worker.onerror = event => { event.preventDefault(); finish(undefined, new Error('Generation worker failed')); };
      worker.postMessage({ ...options, deadlineMs: Math.max(1, Math.min(options.deadlineMs ?? 2000, deadline - performance.now() - 50)) });
    } catch { finish(undefined, new Error('Generation worker failed')); }
  });
}

/** One foreground budget, with time reserved for a smaller permitted fallback. */
export async function generateWithFallback(primary: GenerateOptions, fallback: GenerateOptions, generate: Generator,
  signal?: AbortSignal): Promise<Board> {
  const start = performance.now(), deadline = start + 2000;
  if (signal?.aborted) throw cancelled();
  try { return await generate(primary, signal, start + 1200); }
  catch (error) {
    if (signal?.aborted) throw cancelled();
    if (performance.now() >= deadline) throw error;
    return generate(fallback, signal, deadline);
  }
}
